'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { useInstantSearch, useSearchBox } from 'react-instantsearch';
import { useIsPopNavigation } from 'commons/navState';
import { NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import ProductCardGridUI from 'components/consumer/ProductCardGridUI';
import ProductCardStaticPlaceholderGroup from 'components/consumer/ProductCardStaticPlaceholderGroup';
import useMobileColumnCount from 'components/consumer/ProductListingPage/state/mobileColumnCount/useMobileColumnCount';
import useAdvancedSearchContext from '../../hooks/useAdvancedSearchContext';
import useFilterTransitionState from '../../hooks/useFilterTransitionState';
import AdvancedSearchProductsGrid from '../AdvancedSearchProductsGrid';
import LoadMoreButton from '../AdvancedSearchProductsGrid/LoadMoreButton';
import useHitsAccumulation from './hooks/useHitsAccumulation';
import useDisplayProducts from './hooks/useDisplayProducts';
import useScrollPersistence from './hooks/useScrollPersistence';
const AdvancedSearchProductHits = t0 => {
  const $ = _c(68);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    onProductsChange
  } = t1;
  const {
    items,
    isLastPage,
    showMore
  } = useHitsAccumulation();
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      skipSuspense: true
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    results,
    status,
    setIndexUiState,
    indexUiState
  } = useInstantSearch(t2);
  const {
    query: searchBoxQuery
  } = useSearchBox();
  const mobileColumnCount = useMobileColumnCount();
  const {
    isReadyToShipActive,
    minWidth,
    maxWidth,
    activeTypeFilter,
    pagesUrlParam,
    updateUrlParamImmediate,
    selectedFacets
  } = useAdvancedSearchContext();
  const isPopNavigation = useIsPopNavigation();
  let t3;
  if ($[3] !== isPopNavigation) {
    t3 = isPopNavigation();
    $[3] = isPopNavigation;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const isPopRef = useRef(t3);
  const scrollRestoredRef = useRef(false);
  const targetPagesRef = useRef(pagesUrlParam);
  let t4;
  let t5;
  if ($[5] !== activeTypeFilter || $[6] !== setIndexUiState) {
    t4 = () => {
      setIndexUiState(prev => {
        const currentType = prev?.refinementList?.type?.[0] ?? null;
        if (currentType === activeTypeFilter) {
          return prev;
        }
        const newRefinementList = {
          ...(prev.refinementList || {})
        };
        if (activeTypeFilter) {
          newRefinementList.type = [activeTypeFilter];
        } else {
          delete newRefinementList.type;
        }
        return {
          ...prev,
          refinementList: newRefinementList
        };
      });
    };
    t5 = [activeTypeFilter, setIndexUiState];
    $[5] = activeTypeFilter;
    $[6] = setIndexUiState;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  const currentPageCount = Math.ceil((items?.length || 0) / NUMBER_OF_PRODUCTS_TO_SHOW);
  let t6;
  if ($[9] !== currentPageCount || $[10] !== isLastPage || $[11] !== items?.length || $[12] !== showMore || $[13] !== status) {
    t6 = () => {
      if (status !== "idle" || !items?.length || isLastPage) {
        return;
      }
      if (currentPageCount - 1 < targetPagesRef.current) {
        showMore();
      }
    };
    $[9] = currentPageCount;
    $[10] = isLastPage;
    $[11] = items?.length;
    $[12] = showMore;
    $[13] = status;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = items?.length;
  let t8;
  if ($[15] !== currentPageCount || $[16] !== isLastPage || $[17] !== showMore || $[18] !== status || $[19] !== t7) {
    t8 = [currentPageCount, status, t7, isLastPage, showMore];
    $[15] = currentPageCount;
    $[16] = isLastPage;
    $[17] = showMore;
    $[18] = status;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  useEffect(t6, t8);
  indexUiState?.refinementList;
  let t9;
  if ($[21] !== indexUiState?.refinementList || $[22] !== isReadyToShipActive || $[23] !== maxWidth || $[24] !== minWidth || $[25] !== selectedFacets) {
    t9 = JSON.stringify({
      refinementList: indexUiState?.refinementList || {},
      isReadyToShipActive,
      minWidth,
      maxWidth,
      selectedFacets: selectedFacets ? Object.fromEntries(Object.entries(selectedFacets).map(_temp)) : undefined
    });
    $[21] = indexUiState?.refinementList;
    $[22] = isReadyToShipActive;
    $[23] = maxWidth;
    $[24] = minWidth;
    $[25] = selectedFacets;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const filterStateKey = t9;
  let t10;
  if ($[27] !== activeTypeFilter || $[28] !== filterStateKey || $[29] !== items || $[30] !== setIndexUiState || $[31] !== status || $[32] !== updateUrlParamImmediate) {
    t10 = {
      filterStateKey,
      setIndexUiState,
      items,
      status,
      activeTypeFilter,
      updateUrlParamImmediate,
      isPopRef,
      scrollRestoredRef,
      targetPagesRef
    };
    $[27] = activeTypeFilter;
    $[28] = filterStateKey;
    $[29] = items;
    $[30] = setIndexUiState;
    $[31] = status;
    $[32] = updateUrlParamImmediate;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const {
    isFilterTransitioning,
    hasSearchCompleted,
    showNoResults
  } = useFilterTransitionState(t10);
  let t11;
  if ($[34] !== items || $[35] !== results || $[36] !== searchBoxQuery) {
    t11 = {
      items,
      searchBoxQuery,
      results
    };
    $[34] = items;
    $[35] = results;
    $[36] = searchBoxQuery;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  const {
    displayProducts,
    loading,
    productIds,
    productIdsRaw
  } = useDisplayProducts(t11);
  const t12 = items?.length;
  let t13;
  if ($[38] !== currentPageCount || $[39] !== loading || $[40] !== productIds.length || $[41] !== productIdsRaw.length || $[42] !== status || $[43] !== t12) {
    t13 = {
      isPopRef,
      scrollRestoredRef,
      targetPagesRef,
      status,
      currentPageCount,
      itemsLength: t12,
      loading,
      productIdsLength: productIds.length,
      productIdsRawLength: productIdsRaw.length
    };
    $[38] = currentPageCount;
    $[39] = loading;
    $[40] = productIds.length;
    $[41] = productIdsRaw.length;
    $[42] = status;
    $[43] = t12;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  useScrollPersistence(t13);
  let t14;
  let t15;
  if ($[45] !== displayProducts || $[46] !== onProductsChange) {
    t14 = () => {
      if (onProductsChange && displayProducts?.length > 0) {
        onProductsChange(displayProducts);
      }
    };
    t15 = [displayProducts, onProductsChange];
    $[45] = displayProducts;
    $[46] = onProductsChange;
    $[47] = t14;
    $[48] = t15;
  } else {
    t14 = $[47];
    t15 = $[48];
  }
  useEffect(t14, t15);
  let t16;
  if ($[49] !== pagesUrlParam || $[50] !== showMore || $[51] !== updateUrlParamImmediate) {
    t16 = () => {
      const nextPages = pagesUrlParam + 1;
      showMore();
      targetPagesRef.current = nextPages;
      updateUrlParamImmediate?.({
        pages: nextPages
      });
    };
    $[49] = pagesUrlParam;
    $[50] = showMore;
    $[51] = updateUrlParamImmediate;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  const handleShowMore = t16;
  const isRefreshing = status === "loading" || status === "stalled" || loading;
  if (!items?.length) {
    const showSkeleton = status === "loading" || status === "stalled" || isFilterTransitioning || !hasSearchCompleted || !showNoResults;
    if (showSkeleton) {
      let t17;
      if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = <ProductCardStaticPlaceholderGroup count={NUMBER_OF_PRODUCTS_TO_SHOW} />;
        $[53] = t17;
      } else {
        t17 = $[53];
      }
      let t18;
      if ($[54] !== mobileColumnCount) {
        t18 = <div className="md:space-y-8 md:mb-8 md:flex-1 min-h-[400px]"><ProductCardGridUI limitWidth={false} columnsOnDesktop="auto" columnsOnMobile={mobileColumnCount}>{t17}</ProductCardGridUI></div>;
        $[54] = mobileColumnCount;
        $[55] = t18;
      } else {
        t18 = $[55];
      }
      return t18;
    }
    let t17;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <div role="status" aria-live="polite" className="py-8 text-center">No results found.</div>;
      $[56] = t17;
    } else {
      t17 = $[56];
    }
    return t17;
  }
  const t17 = isRefreshing ? "animate-pulse" : "";
  let t18;
  if ($[57] !== displayProducts) {
    t18 = displayProducts?.length > 0 && <AdvancedSearchProductsGrid products={displayProducts} />;
    $[57] = displayProducts;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== displayProducts?.length || $[60] !== handleShowMore || $[61] !== isLastPage || $[62] !== loading) {
    t19 = displayProducts?.length > 0 && !isLastPage && <div className="flex justify-center mt-8 mb-12"><LoadMoreButton onClick={handleShowMore} isLoading={loading} /></div>;
    $[59] = displayProducts?.length;
    $[60] = handleShowMore;
    $[61] = isLastPage;
    $[62] = loading;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  let t20;
  if ($[64] !== t17 || $[65] !== t18 || $[66] !== t19) {
    t20 = <div className={t17}>{t18}{t19}</div>;
    $[64] = t17;
    $[65] = t18;
    $[66] = t19;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  return t20;
};
export default AdvancedSearchProductHits;
function _temp(t0) {
  const [attr, values] = t0;
  return [attr, [...values].sort()];
}